// apps/client/src/apps/analytics/useGlobalTrackedClicks.ts
import { useEffect, useRef } from "react";
import { safeGtagEvent } from "./track";

type Options = {
  // Optional: add extra params to every event (e.g., hospital)
  getBaseParams?: () => Record<string, unknown>;
  // Optional: prevent tracking in some cases
  shouldTrack?: (el: Element, e: Event) => boolean;
};

function isActivationKey(e: KeyboardEvent) {
  return e.key === "Enter" || e.key === " ";
}

export function useGlobalTrackedClicks(options?: Options) {
  // Keep options in a ref so handlers always read the latest values
  // without needing to re-bind listeners on every render.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;

      const el = target.closest?.("[data-track-id]");
      if (!el) return;

      if (optionsRef.current?.shouldTrack && !optionsRef.current.shouldTrack(el, e)) return;

      const trackId = el.getAttribute("data-track-id");
      if (!trackId) return;

      const baseParams = optionsRef.current?.getBaseParams?.() ?? {};
      safeGtagEvent(`${trackId}_click`, {
        ...baseParams,
        tag: el.tagName.toLowerCase(),
        href: (el as HTMLAnchorElement).href || undefined,
      });
    };

    const onKeydownCapture = (e: KeyboardEvent) => {
      if (!isActivationKey(e)) return;

      const target = e.target as Element | null;
      if (!target) return;

      const el = target.closest?.("[data-track-id]");
      if (!el) return;

      if (optionsRef.current?.shouldTrack && !optionsRef.current.shouldTrack(el, e)) return;

      const trackId = el.getAttribute("data-track-id");
      if (!trackId) return;

      const baseParams = optionsRef.current?.getBaseParams?.() ?? {};
      safeGtagEvent(`${trackId}_click`, {
        ...baseParams,
        tag: el.tagName.toLowerCase(),
      });
    };

    // Use capture phase to catch clicks even if some component stops propagation.
    document.addEventListener("click", onClickCapture, true);
    document.addEventListener("keydown", onKeydownCapture, true);

    return () => {
      document.removeEventListener("click", onClickCapture, true);
      document.removeEventListener("keydown", onKeydownCapture, true);
    };
  }, []); // Listeners are attached once; latest options are read via ref at event time.
}